import clsx from 'clsx'
import moment from 'moment'
import {FC, useEffect, useMemo, useState} from 'react'
import {Container, Modal} from 'react-bootstrap'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../../../_gori/helpers'
import {Button, SelectNoFormik} from '../../../../_gori/partials/widgets'
import {
  hasActiveAccountFilters,
  isRangeComparisonOperator,
} from '../helpers/organizationAccountsFilterHelpers'

const FILTER_FIELD_BORDER_COLOR = '#14B8A6'

type FilterFieldClearProps = {
  onClick: () => void
  ariaLabel: string
}

const FilterFieldClear: FC<FilterFieldClearProps> = ({onClick, ariaLabel}) => (
  <button
    type='button'
    className='organization-accounts-filter__clear'
    aria-label={ariaLabel}
    onClick={onClick}
  >
    <KTSVG path='/media/gori/orders/delete.svg' className='svg-icon svg-icon-6' small={false} />
  </button>
)

export type ComparisonOperator = 'gt' | 'lt' | 'gte' | 'lte' | 'between' | 'not_between' | 'neq'

export type OrganizationAccountsFilterValues = {
  accountCreationDateFrom: string | null
  accountCreationDateTo: string | null
  lastShipmentDateFrom: string | null
  lastShipmentDateTo: string | null
  totalSpendOperator: ComparisonOperator
  totalSpendValue: string
  totalSpendValueTo: string
  totalVolumeOperator: ComparisonOperator
  totalVolumeValue: string
  totalVolumeValueTo: string
}

type DateRangeValue = {
  start: moment.Moment | null
  end: moment.Moment | null
}

type Props = {
  show: boolean
  handleClose: () => void
  onApply?: (filters: OrganizationAccountsFilterValues) => void
  initialValues?: Partial<OrganizationAccountsFilterValues>
}

const DEFAULT_FILTER_VALUES: OrganizationAccountsFilterValues = {
  accountCreationDateFrom: null,
  accountCreationDateTo: null,
  lastShipmentDateFrom: null,
  lastShipmentDateTo: null,
  totalSpendOperator: 'gt',
  totalSpendValue: '',
  totalSpendValueTo: '',
  totalVolumeOperator: 'gt',
  totalVolumeValue: '',
  totalVolumeValueTo: '',
}

const parseDateRange = (from: string | null, to: string | null): DateRangeValue => ({
  start: from ? moment(from) : null,
  end: to ? moment(to) : null,
})

const formatDateRangeLabel = (
  range: DateRangeValue,
  placeholder: string,
  dateFormat: string
): string => {
  if (!range.start || !range.end) {
    return placeholder
  }
  return `${range.start.format(dateFormat)} - ${range.end.format(dateFormat)}`
}

const resolvePresetRangeLabel = (
  range: DateRangeValue,
  ranges: Record<string, moment.Moment[]>
): string | null => {
  if (!range.start || !range.end) {
    return null
  }

  for (const [label, [start, end]] of Object.entries(ranges)) {
    if (range.start.isSame(start, 'day') && range.end.isSame(end, 'day')) {
      return label
    }
  }

  return null
}

const getDateRangeDisplayLabel = (
  range: DateRangeValue,
  selectedRangeLabel: string | null,
  ranges: Record<string, moment.Moment[]>,
  customRangeLabel: string,
  placeholder: string,
  dateFormat: string
): string => {
  if (!range.start || !range.end) {
    return placeholder
  }

  if (selectedRangeLabel && selectedRangeLabel !== customRangeLabel) {
    return selectedRangeLabel
  }

  const presetLabel = resolvePresetRangeLabel(range, ranges)
  if (presetLabel) {
    return presetLabel
  }

  return formatDateRangeLabel(range, placeholder, dateFormat)
}

type DateRangeFilterFieldProps = {
  label: string
  value: DateRangeValue
  selectedRangeLabel: string | null
  placeholder: string
  customRangeLabel: string
  locale: Record<string, unknown>
  ranges: Record<string, moment.Moment[]>
  onApply: (start: moment.Moment, end: moment.Moment, rangeLabel: string | null) => void
  onClear: () => void
  clearAriaLabel: string
}

const DateRangeFilterField: FC<DateRangeFilterFieldProps> = ({
  label,
  value,
  selectedRangeLabel,
  placeholder,
  customRangeLabel,
  locale,
  ranges,
  onApply,
  onClear,
  clearAriaLabel,
}) => {
  const dateFormat = process.env.REACT_APP_DATE_FORMAT ?? 'MM/DD/YYYY'
  const displayLabel = getDateRangeDisplayLabel(
    value,
    selectedRangeLabel,
    ranges,
    customRangeLabel,
    placeholder,
    dateFormat
  )
  const hasValue = Boolean(value.start && value.end)

  return (
    <div className='organization-accounts-filter__field'>
      <label className='organization-accounts-filter__label'>{label}</label>
      <div className='organization-accounts-filter__control-row'>
        <div
          className={clsx('organization-accounts-filter__control', {
            'organization-accounts-filter__control--filled': hasValue,
          })}
        >
          <DateRangePicker
            initialSettings={{
              locale,
              startDate: value.start ?? moment().subtract(29, 'days'),
              endDate: value.end ?? moment(),
              opens: 'left',
              ranges,
              alwaysShowCalendars: false,
              maxDate: moment(),
            }}
            onApply={(_event, picker) =>
              onApply(picker.startDate, picker.endDate, picker.chosenLabel ?? null)
            }
          >
            <button
              type='button'
              className={clsx(
                'organization-accounts-filter__date-trigger form-select text-start w-100',
                {'text-muted': !hasValue}
              )}
            >
              {displayLabel}
            </button>
          </DateRangePicker>
        </div>
        {hasValue && <FilterFieldClear onClick={onClear} ariaLabel={clearAriaLabel} />}
      </div>
    </div>
  )
}

type ComparisonFilterFieldProps = {
  label: string
  operator: ComparisonOperator
  value: string
  valueTo: string
  operatorOptions: {value: ComparisonOperator; label: string}[]
  onOperatorChange: (operator: ComparisonOperator) => void
  onValueChange: (value: string) => void
  onValueToChange: (value: string) => void
  onClear: () => void
  clearAriaLabel: string
  inputPlaceholder: string
  valueToPlaceholder: string
  showCurrencyPrefix?: boolean
}

const ComparisonFilterField: FC<ComparisonFilterFieldProps> = ({
  label,
  operator,
  value,
  valueTo,
  operatorOptions,
  onOperatorChange,
  onValueChange,
  onValueToChange,
  onClear,
  clearAriaLabel,
  inputPlaceholder,
  valueToPlaceholder,
  showCurrencyPrefix = false,
}) => {
  const intl = useIntl()
  const showValueTo = isRangeComparisonOperator(operator)
  const numericPattern = showCurrencyPrefix ? /[^0-9.]/g : /[^0-9]/g
  const formatValue = (raw: string) => {
    if (!raw) return ''
    const num = Number(raw)
    return Number.isNaN(num) ? raw : intl.formatNumber(num)
  }
  const hasValue = value.trim() !== '' || (showValueTo && valueTo.trim() !== '')
  const filledInputStyle = hasValue
    ? {borderColor: FILTER_FIELD_BORDER_COLOR, boxShadow: 'none'}
    : undefined

  return (
    <div className='organization-accounts-filter__field'>
      <label className='organization-accounts-filter__label'>{label}</label>
      <div className='organization-accounts-filter__control-row'>
        <div
          className={clsx('organization-accounts-filter__control', {
            'organization-accounts-filter__control--filled': hasValue,
          })}
        >
          <div className='organization-accounts-filter__comparison-row'>
            <div className='organization-accounts-filter__operator'>
              <SelectNoFormik
                options={operatorOptions}
                value={operatorOptions.find((option) => option.value === operator)}
                onChange={(option: {value: ComparisonOperator}) => onOperatorChange(option.value)}
                isSearchable={false}
                styleInput={filledInputStyle}
              />
            </div>
            <div
              className={clsx('organization-accounts-filter__values', {
                'organization-accounts-filter__values--range': showValueTo,
              })}
            >
              <div
                className={clsx('organization-accounts-filter__value', {
                  'organization-accounts-filter__value--filled': value.trim() !== '',
                })}
              >
                {showCurrencyPrefix && (
                  <span className='input-group-text pe-0' style={filledInputStyle}>
                    $
                  </span>
                )}
                <div className='flex-grow-1 min-w-0 w-100'>
                  <div className='input-group'>
                    <input
                      type='text'
                      autoComplete='off'
                      className={clsx('form-control', showCurrencyPrefix ? 'ps-0' : '')}
                      value={formatValue(value)}
                      placeholder={inputPlaceholder}
                      onChange={(event) =>
                        onValueChange(event.target.value.replace(numericPattern, ''))
                      }
                    />
                  </div>
                </div>
              </div>
              {showValueTo && (
                <>
                  <span>
                    <KTSVG
                      path='/media/gori/orders/minus.svg'
                      className='svg-icon-2 branches-menu__toggle-icon'
                    />
                  </span>
                  <div
                    className={clsx('organization-accounts-filter__value', {
                      'organization-accounts-filter__value--filled': valueTo.trim() !== '',
                    })}
                  >
                    {showCurrencyPrefix && (
                      <span className='input-group-text pe-0' style={filledInputStyle}>
                        $
                      </span>
                    )}
                    <div className='flex-grow-1 min-w-0 w-100'>
                      <div className='input-group'>
                        <input
                          type='text'
                          autoComplete='off'
                          className={clsx('form-control', showCurrencyPrefix ? 'ps-0' : '')}
                          value={formatValue(valueTo)}
                          placeholder={valueToPlaceholder}
                          onChange={(event) =>
                            onValueToChange(event.target.value.replace(numericPattern, ''))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {hasValue && <FilterFieldClear onClick={onClear} ariaLabel={clearAriaLabel} />}
      </div>
    </div>
  )
}

const OrganizationAccountsFilter: FC<Props> = ({show, handleClose, onApply, initialValues}) => {
  const intl = useIntl()
  const [filters, setFilters] = useState<OrganizationAccountsFilterValues>(DEFAULT_FILTER_VALUES)
  const [accountCreationDate, setAccountCreationDate] = useState<DateRangeValue>({
    start: null,
    end: null,
  })
  const [lastShipmentDate, setLastShipmentDate] = useState<DateRangeValue>({
    start: null,
    end: null,
  })
  const [accountCreationDateLabel, setAccountCreationDateLabel] = useState<string | null>(null)
  const [lastShipmentDateLabel, setLastShipmentDateLabel] = useState<string | null>(null)

  const customRangeLabel = intl.formatMessage({id: 'CUSTOM_RANGE'})

  const dateRangePickerRanges = useMemo(
    () => ({
      [intl.formatMessage({id: 'TODAY'})]: [moment(), moment()],
      [intl.formatMessage({id: 'YESTERDAY'})]: [
        moment().subtract(1, 'days'),
        moment().subtract(1, 'days'),
      ],
      [intl.formatMessage({id: 'LAST_7_DAYS'})]: [moment().subtract(6, 'days'), moment()],
      [intl.formatMessage({id: 'LAST_30_DAYS'})]: [moment().subtract(29, 'days'), moment()],
      [intl.formatMessage({id: 'LAST_MONTH'})]: [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    }),
    [intl]
  )

  const dateRangePickerLocale = useMemo(
    () => ({
      customRangeLabel,
      cancelLabel: intl.formatMessage({id: 'CANCEL'}),
      applyLabel: intl.formatMessage({id: 'APPLY'}),
      fromLabel: intl.formatMessage({id: 'FROM'}),
      toLabel: intl.formatMessage({id: 'TO'}),
      daysOfWeek: [
        intl.formatMessage({id: 'SU'}),
        intl.formatMessage({id: 'MO'}),
        intl.formatMessage({id: 'TU'}),
        intl.formatMessage({id: 'WE'}),
        intl.formatMessage({id: 'TH'}),
        intl.formatMessage({id: 'FR'}),
        intl.formatMessage({id: 'SA'}),
      ],
      monthNames: [
        intl.formatMessage({id: 'JANUARY'}),
        intl.formatMessage({id: 'FEBRUARY'}),
        intl.formatMessage({id: 'MARCH'}),
        intl.formatMessage({id: 'APRIL'}),
        intl.formatMessage({id: 'MAY'}),
        intl.formatMessage({id: 'JUNE'}),
        intl.formatMessage({id: 'JULY'}),
        intl.formatMessage({id: 'AUGUST'}),
        intl.formatMessage({id: 'SEPTEMBER'}),
        intl.formatMessage({id: 'OCTOBER'}),
        intl.formatMessage({id: 'NOVEMBER'}),
        intl.formatMessage({id: 'DECEMBER'}),
      ],
    }),
    [customRangeLabel, intl]
  )

  const hasSelectedFilters = useMemo(() => hasActiveAccountFilters(filters), [filters])

  const comparisonOperatorOptions = useMemo(
    () => [
      {value: 'gt' as ComparisonOperator, label: intl.formatMessage({id: 'GREATER_THAN'})},
      {
        value: 'gte' as ComparisonOperator,
        label: intl.formatMessage({id: 'GREATER_THAN_OR_EQUAL_TO'}),
      },
      {value: 'lt' as ComparisonOperator, label: intl.formatMessage({id: 'LESS_THAN'})},
      {
        value: 'lte' as ComparisonOperator,
        label: intl.formatMessage({id: 'LESS_THAN_OR_EQUAL_TO'}),
      },
      {value: 'neq' as ComparisonOperator, label: intl.formatMessage({id: 'NOT_EQUAL_TO'})},
      {value: 'between' as ComparisonOperator, label: intl.formatMessage({id: 'BETWEEN'})},
      {value: 'not_between' as ComparisonOperator, label: intl.formatMessage({id: 'NOT_BETWEEN'})},
    ],
    [intl]
  )

  useEffect(() => {
    if (!show) {
      return
    }

    const nextFilters = {...DEFAULT_FILTER_VALUES, ...initialValues}
    const nextAccountCreationDate = parseDateRange(
      nextFilters.accountCreationDateFrom,
      nextFilters.accountCreationDateTo
    )
    const nextLastShipmentDate = parseDateRange(
      nextFilters.lastShipmentDateFrom,
      nextFilters.lastShipmentDateTo
    )

    setFilters(nextFilters)
    setAccountCreationDate(nextAccountCreationDate)
    setLastShipmentDate(nextLastShipmentDate)
    setAccountCreationDateLabel(
      resolvePresetRangeLabel(nextAccountCreationDate, dateRangePickerRanges)
    )
    setLastShipmentDateLabel(resolvePresetRangeLabel(nextLastShipmentDate, dateRangePickerRanges))
  }, [dateRangePickerRanges, initialValues, show])

  const handleCloseModal = () => {
    handleClose()
  }

  const handleCancel = () => {
    const resetFilters = {...DEFAULT_FILTER_VALUES, ...initialValues}
    const resetAccountCreationDate = parseDateRange(
      resetFilters.accountCreationDateFrom,
      resetFilters.accountCreationDateTo
    )
    const resetLastShipmentDate = parseDateRange(
      resetFilters.lastShipmentDateFrom,
      resetFilters.lastShipmentDateTo
    )

    setFilters(resetFilters)
    setAccountCreationDate(resetAccountCreationDate)
    setLastShipmentDate(resetLastShipmentDate)
    setAccountCreationDateLabel(
      resolvePresetRangeLabel(resetAccountCreationDate, dateRangePickerRanges)
    )
    setLastShipmentDateLabel(resolvePresetRangeLabel(resetLastShipmentDate, dateRangePickerRanges))
    handleCloseModal()
  }

  const handleApply = () => {
    onApply?.(filters)
    handleCloseModal()
  }

  const selectDateRangePlaceholder = intl.formatMessage({id: 'SELECT_DATE_RANGE'})
  const inputValuePlaceholder = intl.formatMessage({id: 'INPUT_VALUE'})
  const inputValueToPlaceholder = intl.formatMessage({id: 'INPUT_VALUE_TO'})

  return (
    <Modal
      id='gori_modal_organization_accounts_filter'
      tabIndex={-1}
      aria-hidden='true'
      centered
      dialogClassName='modal-size-md h-auto'
      contentClassName='modal-padding'
      show={show}
      backdrop='static'
      onHide={handleCloseModal}
    >
      <Modal.Header closeButton>
        <Modal.Title bsPrefix='fw-bolder fs-1 modal-title'>
          {intl.formatMessage({id: 'ACCOUNT_FILTERS'})}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='organization-accounts-filter'>
        <Container>
          <DateRangeFilterField
            label={intl.formatMessage({id: 'ACCOUNT_CREATION_DATE'})}
            value={accountCreationDate}
            selectedRangeLabel={accountCreationDateLabel}
            placeholder={selectDateRangePlaceholder}
            customRangeLabel={customRangeLabel}
            locale={dateRangePickerLocale}
            ranges={dateRangePickerRanges}
            onApply={(start, end, rangeLabel) => {
              setAccountCreationDate({start: moment(start), end: moment(end)})
              setAccountCreationDateLabel(rangeLabel)
              setFilters((prev) => ({
                ...prev,
                accountCreationDateFrom: moment(start).format(),
                accountCreationDateTo: moment(end).format(),
              }))
            }}
            onClear={() => {
              setAccountCreationDate({start: null, end: null})
              setAccountCreationDateLabel(null)
              setFilters((prev) => ({
                ...prev,
                accountCreationDateFrom: null,
                accountCreationDateTo: null,
              }))
            }}
            clearAriaLabel={intl.formatMessage({id: 'REMOVE'})}
          />
          <DateRangeFilterField
            label={intl.formatMessage({id: 'LAST_SHIPMENT_DATE'})}
            value={lastShipmentDate}
            selectedRangeLabel={lastShipmentDateLabel}
            placeholder={selectDateRangePlaceholder}
            customRangeLabel={customRangeLabel}
            locale={dateRangePickerLocale}
            ranges={dateRangePickerRanges}
            onApply={(start, end, rangeLabel) => {
              setLastShipmentDate({start: moment(start), end: moment(end)})
              setLastShipmentDateLabel(rangeLabel)
              setFilters((prev) => ({
                ...prev,
                lastShipmentDateFrom: moment(start).format(),
                lastShipmentDateTo: moment(end).format(),
              }))
            }}
            onClear={() => {
              setLastShipmentDate({start: null, end: null})
              setLastShipmentDateLabel(null)
              setFilters((prev) => ({
                ...prev,
                lastShipmentDateFrom: null,
                lastShipmentDateTo: null,
              }))
            }}
            clearAriaLabel={intl.formatMessage({id: 'REMOVE'})}
          />
          <ComparisonFilterField
            label={intl.formatMessage({id: 'TOTAL_SHIPMENT_SPEND'})}
            operator={filters.totalSpendOperator}
            value={filters.totalSpendValue}
            valueTo={filters.totalSpendValueTo}
            operatorOptions={comparisonOperatorOptions}
            onOperatorChange={(operator) =>
              setFilters((prev) => ({
                ...prev,
                totalSpendOperator: operator,
                totalSpendValueTo: isRangeComparisonOperator(operator)
                  ? prev.totalSpendValueTo
                  : '',
              }))
            }
            onValueChange={(value) => setFilters((prev) => ({...prev, totalSpendValue: value}))}
            onValueToChange={(value) => setFilters((prev) => ({...prev, totalSpendValueTo: value}))}
            onClear={() =>
              setFilters((prev) => ({
                ...prev,
                totalSpendValue: '',
                totalSpendValueTo: '',
                totalSpendOperator: 'gt',
              }))
            }
            clearAriaLabel={intl.formatMessage({id: 'REMOVE'})}
            inputPlaceholder={inputValuePlaceholder}
            valueToPlaceholder={inputValueToPlaceholder}
            showCurrencyPrefix
          />
          <ComparisonFilterField
            label={intl.formatMessage({id: 'TOTAL_SHIPMENT_VOLUME'})}
            operator={filters.totalVolumeOperator}
            value={filters.totalVolumeValue}
            valueTo={filters.totalVolumeValueTo}
            operatorOptions={comparisonOperatorOptions}
            onOperatorChange={(operator) =>
              setFilters((prev) => ({
                ...prev,
                totalVolumeOperator: operator,
                totalVolumeValueTo: isRangeComparisonOperator(operator)
                  ? prev.totalVolumeValueTo
                  : '',
              }))
            }
            onValueChange={(value) => setFilters((prev) => ({...prev, totalVolumeValue: value}))}
            onValueToChange={(value) =>
              setFilters((prev) => ({...prev, totalVolumeValueTo: value}))
            }
            onClear={() =>
              setFilters((prev) => ({
                ...prev,
                totalVolumeValue: '',
                totalVolumeValueTo: '',
                totalVolumeOperator: 'gt',
              }))
            }
            clearAriaLabel={intl.formatMessage({id: 'REMOVE'})}
            inputPlaceholder={inputValuePlaceholder}
            valueToPlaceholder={inputValueToPlaceholder}
          />
        </Container>
      </Modal.Body>
      <Modal.Footer className='border-top'>
        <div className='d-flex justify-content-end w-100 gap-3'>
          <Button
            className='btn btn-advanced-secondary'
            label={intl.formatMessage({id: 'CANCEL'})}
            loadingText={intl.formatMessage({id: 'CANCEL'})}
            event={handleCancel}
          />
          <Button
            className='btn btn-advanced-primary'
            label={intl.formatMessage({id: 'APPLY'})}
            loadingText={intl.formatMessage({id: 'APPLY'})}
            event={handleApply}
            disabled={!hasSelectedFilters}
          />
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export {OrganizationAccountsFilter}
